import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { defineMessages, useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import OfferCardCarouselSectionFilters from 'components/Common/OfferCardCarousel/OfferCardCarouselSectionFilters'
import OfferListCarousel from 'components/OfferList/OfferListCarousel'
import { OfferListEventsProvider } from 'components/OfferList/OfferListEventsContext'
import LayoutContainer from 'components/Common/LayoutContainer/LayoutContainer'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Group from 'components/utils/Group'
import Heading from 'LuxKit/Typography/Heading'
import TextButton from 'LuxKit/Button/TextButton'
import useOfferList from 'hooks/Offers/useOfferList'
import useOfferListTracking from 'hooks/Offers/useOfferListTracking'
import { getListName } from 'analytics/enhanced-ecommerce'
import Places from 'constants/places'
import { getSearchQueryFromFilter } from 'lib/search/searchUtils'
import LuxPlusLandingNavigationContext from 'luxPlus/contexts/LuxPlusLandingNavigationContext'

export type OfferCategory = 'member-discounts' | 'bonus-inclusions' | 'hidden-offers' | 'early-access'

const CATEGORY_MESSAGES = defineMessages<OfferCategory>({
  'member-discounts': {
    id: 'dkNNP4tZfeaxB2VipgxU',
    defaultMessage: 'Member-only discounts',
    description: 'LuxPlus offer category label for member-only discounts',
  },
  'bonus-inclusions': {
    id: 'U5hqejvcAztRnBGy7iOR',
    defaultMessage: 'Bonus inclusions',
    description: 'LuxPlus offer carousel category label for bonus inclusions',
  },
  'hidden-offers': {
    id: '_VW1eYnoTkralpHzVdU2',
    defaultMessage: 'Hidden Offers',
    description: 'LuxPlus offer category label for hidden/exclusive offers',
  },
  'early-access': {
    id: 'AGbDcOu6cZ-Gs40lOYgN',
    defaultMessage: 'Early Access',
    description: 'LuxPlus offer category label for early access offers',
  },
})

const ALL_CATEGORY_KEYS: Array<OfferCategory> = [
  'member-discounts',
  'bonus-inclusions',
  'hidden-offers',
  'early-access',
]

const SectionContainer = styled(VerticalSpacer)`
  padding-top: ${rem(40)};
  padding-bottom: ${rem(40)};
`

function getFiltersForCategory(
  category: OfferCategory,
  offerTypes: App.OfferListFilters['offerTypes'],
  sortBy: App.OfferListSortByOptions,
): App.OfferListFilters {
  switch (category) {
    case 'member-discounts':
      return {
        offerTypes,
        luxPlusFeatures: ['Member prices'],
        destinations: [Places.Anywhere.id],
        sortBy,
        limit: 20,
      }
    case 'bonus-inclusions':
      return {
        offerTypes,
        luxPlusFeatures: ['Bonus inclusions'],
        destinations: [Places.Anywhere.id],
        sortBy,
        limit: 20,
      }
    case 'hidden-offers':
      return {
        offerTypes,
        luxPlusFeatures: ['Member exclusive'],
        destinations: [Places.Anywhere.id],
        scheduleVisibilities: ['luxPlusExclusiveVisible'],
        limit: 20,
      }
    case 'early-access':
      return {
        offerTypes,
        luxPlusFeatures: ['Early access'],
        destinations: [Places.Anywhere.id],
        sortBy,
        limit: 20,
      }
  }
}

interface Props {
  title: string
  offerTypes: App.OfferListFilters['offerTypes']
  sortBy: App.OfferListSortByOptions
  categories?: Array<OfferCategory>
  analyticsPage: string
  allLinkBase?: string
  carouselIdPrefix: string
}

function LuxPlusFilteredOfferCarousel({
  title,
  offerTypes,
  sortBy,
  categories,
  analyticsPage,
  allLinkBase = '/search',
  carouselIdPrefix,
}: Props) {
  const intl = useIntl()

  // Memo needed to prevent unnecessary re-renders of all subsequent memoizations
  const allCategories = useMemo(
    () => ALL_CATEGORY_KEYS.map(key => ({ key, label: intl.formatMessage(CATEGORY_MESSAGES[key]) })),
    [intl],
  )

  const availableCategories = useMemo(
    () => categories
      ? allCategories.filter(c => categories.includes(c.key))
      : allCategories,
    [categories, allCategories],
  )

  const filtersByCategory = useMemo(() => ({
    'member-discounts': getFiltersForCategory('member-discounts', offerTypes, sortBy),
    'bonus-inclusions': getFiltersForCategory('bonus-inclusions', offerTypes, sortBy),
    'hidden-offers': getFiltersForCategory('hidden-offers', offerTypes, sortBy),
    'early-access': getFiltersForCategory('early-access', offerTypes, sortBy),
  }), [offerTypes, sortBy])

  const listMember = useOfferList(filtersByCategory['member-discounts'], {
    disabled: !availableCategories.some(c => c.key === 'member-discounts'),
  })
  const listBonus = useOfferList(filtersByCategory['bonus-inclusions'], {
    disabled: !availableCategories.some(c => c.key === 'bonus-inclusions'),
  })
  const listHidden = useOfferList(filtersByCategory['hidden-offers'], {
    disabled: !availableCategories.some(c => c.key === 'hidden-offers'),
  })
  const listEarly = useOfferList(filtersByCategory['early-access'], {
    disabled: !availableCategories.some(c => c.key === 'early-access'),
  })

  const offerListsByCategory = useMemo(() => ({
    'member-discounts': listMember,
    'bonus-inclusions': listBonus,
    'hidden-offers': listHidden,
    'early-access': listEarly,
  }), [listMember, listBonus, listHidden, listEarly])

  const categoriesWithOffers = useMemo(() => availableCategories.filter(({ key }) => {
    const list = offerListsByCategory[key]
    return !list.fetching && list.offerIds.length > 0
  }), [availableCategories, offerListsByCategory])

  const { registerFilterCategories } = useContext(LuxPlusLandingNavigationContext)
  const availableCategoryKeys = useMemo(
    () => categoriesWithOffers.map(c => c.key),
    [categoriesWithOffers],
  )
  useEffect(() => {
    registerFilterCategories(carouselIdPrefix, availableCategoryKeys)
  }, [carouselIdPrefix, availableCategoryKeys, registerFilterCategories])

  const [activeCategory, setActiveCategory] = useState<OfferCategory>(availableCategories[0].key)
  const sectionRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const el = sectionRef.current
    if (!el) return
    const handler = (e: Event) => {
      if (!(e instanceof CustomEvent)) return
      const { category } = e.detail
      if (!category || !ALL_CATEGORY_KEYS.includes(category)) return
      setActiveCategory(category)
    }

    el.addEventListener('luxplus-filter-select', handler)
    return () => el.removeEventListener('luxplus-filter-select', handler)
  }, [])

  useEffect(() => {
    if (categoriesWithOffers.length === 0) return
    const currentHasOffers = categoriesWithOffers.some(c => c.key === activeCategory)
    if (!currentHasOffers) {
      setActiveCategory(categoriesWithOffers[0].key)
    }
  }, [activeCategory, categoriesWithOffers])

  const filterOptions = useMemo(
    () => categoriesWithOffers.map(({ key, label }) => ({ value: key, label })),
    [categoriesWithOffers],
  )

  const handleCategoryChange = useCallback((value: string) => {
    setActiveCategory(value as OfferCategory)
  }, [])

  const filters = useMemo(
    () => getFiltersForCategory(activeCategory, offerTypes, sortBy),
    [activeCategory, offerTypes, sortBy],
  )

  const allLink = useMemo(
    () => `${allLinkBase}?${getSearchQueryFromFilter(filters)}`,
    [allLinkBase, filters],
  )

  const tracking = useOfferListTracking(getListName('OfferList', analyticsPage, title))

  const anyStillLoading = listMember.fetching || listBonus.fetching || listHidden.fetching || listEarly.fetching
  if (!anyStillLoading && categoriesWithOffers.length === 0) return null

  return (
    <OfferListEventsProvider tracking={tracking}>
      <SectionContainer ref={sectionRef} gap={20} id={carouselIdPrefix}>
        <LayoutContainer>
          <VerticalSpacer gap={20}>
            <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="start" gap={16}>
              <Heading variant="heading2">{title}</Heading>
              <TextButton kind="tertiary" size="medium" to={allLink}>
                {intl.formatMessage({
                  id: '5_ko7AS6tNN2oH_BeHxU',
                  defaultMessage: 'View all',
                  description: 'Button to view all offers in the current LuxPlus category',
                })}
              </TextButton>
            </Group>
            {filterOptions.length > 1 && <OfferCardCarouselSectionFilters
              options={filterOptions}
              value={activeCategory}
              onChange={handleCategoryChange}
            />}
          </VerticalSpacer>
        </LayoutContainer>
        <OfferListCarousel
          tileSize="medium"
          filters={filters}
          limit={8}
          tabletLimit="default"
        />
      </SectionContainer>
    </OfferListEventsProvider>
  )
}

export default LuxPlusFilteredOfferCarousel
